import React, { useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, Stack, Typography, useTheme } from '@mui/material';
import { CloudUploadOutlined as UploadIcon } from '@mui/icons-material';
import { FormSection, PageTitle } from '@/components/shell';
import { FORM_MAX_WIDTH } from '@/lightTheme';
import { KycOperationType, DocumentSide } from '@/types';
import { OCR_DUAL_SIDE_OPERATIONS } from '@/config/constants';
import { ocrTestingService } from './ocrTestingService';
import { CaseInfoFields, type CaseInfo } from './CaseInfoFields';
import { pickImage } from './pickImage';

const BACK_TO = '/ocr-testing/cases';

/** One side's image: drop it or click (or press Enter) to pick it; once chosen, it can be removed. */
const FileDropZone: React.FC<{ label: string; file: File | null; onFileChange: (file: File | null) => void }> = ({
  label, file, onFileChange,
}) => {
  const t = useTranslate();
  const theme = useTheme();

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={t('screens.ocrTesting.dropImage', { side: label })}
      onClick={() => pickImage(onFileChange)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          pickImage(onFileChange);
        }
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const dropped = e.dataTransfer.files[0];
        if (dropped?.type.startsWith('image/')) onFileChange(dropped);
      }}
      sx={{
        border: '2px dashed',
        borderColor: file ? 'success.main' : 'divider',
        borderRadius: theme.custom.radius.card,
        p: 3,
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'border-color 0.2s',
        '&:hover, &:focus-visible': { borderColor: 'primary.main', outline: 'none' },
      }}
    >
      <UploadIcon sx={{ fontSize: 32, color: file ? 'success.dark' : 'text.secondary', mb: 1 }} />
      <Typography variant="body2" sx={{ color: file ? 'success.dark' : 'text.secondary', overflowWrap: 'anywhere' }}>
        {file ? file.name : t('screens.ocrTesting.dropImage', { side: label })}
      </Typography>
      {file && (
        <Button
          variant="outlined"
          onClick={(e) => { e.stopPropagation(); onFileChange(null); }}
          sx={{ mt: 1 }}
        >
          {t('screens.ocrTesting.removeImage')}
        </Button>
      )}
    </Box>
  );
};

export const TestCaseCreate: React.FC = () => {
  const t = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();

  const [info, setInfo] = useState<CaseInfo>({
    name: '',
    description: '',
    documentTypeName: '',
    nationalityCode: '',
    operationType: KycOperationType.KycSingleSide,
  });
  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [backFile, setBackFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const isDualSide = OCR_DUAL_SIDE_OPERATIONS.includes(info.operationType);

  const handleSubmit = async () => {
    if (!info.name.trim()) {
      notify('screens.ocrTesting.nameRequired', { type: 'warning' });
      return;
    }
    if (!frontFile) {
      notify('screens.ocrTesting.frontRequired', { type: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', info.name.trim());
      if (info.description) formData.append('description', info.description);
      if (info.documentTypeName) formData.append('documentTypeName', info.documentTypeName);
      if (info.nationalityCode) formData.append('nationalityCode', info.nationalityCode);
      formData.append('operationType', String(info.operationType));
      // Backend expects images[] + sides[] (parallel arrays) — see OcrTestController.CreateTestCase
      formData.append('images', frontFile);
      formData.append('sides', String(DocumentSide.Front));
      if (isDualSide && backFile) {
        formData.append('images', backFile);
        formData.append('sides', String(DocumentSide.Back));
      }

      const { data: created } = await ocrTestingService.createCase(formData);
      notify('screens.ocrTesting.caseCreated', { type: 'success' });
      navigate(`/ocr-testing/cases/${created.id}`);
    } catch {
      notify('screens.ocrTesting.caseCreateFailed', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ pt: 1.5, pb: 3, maxWidth: FORM_MAX_WIDTH }}>
      <PageTitle
        title={t('screens.ocrTesting.createTitle')}
        subtitle={t('screens.ocrTesting.createSubtitle')}
        backTo={BACK_TO}
      />

      <Card sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={3}>
          <FormSection title={t('screens.ocrTesting.caseInfo')}>
            <CaseInfoFields value={info} onChange={(patch) => setInfo((prev) => ({ ...prev, ...patch }))} />
          </FormSection>

          <FormSection title={t('screens.ocrTesting.images')} description={t('screens.ocrTesting.imagesHint')}>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: isDualSide ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' } }}>
              <FileDropZone label={t('screens.ocrTesting.front')} file={frontFile} onFileChange={setFrontFile} />
              {isDualSide && (
                <FileDropZone label={t('screens.ocrTesting.back')} file={backFile} onFileChange={setBackFile} />
              )}
            </Box>
          </FormSection>
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, mt: 3 }}>
          <Button variant="outlined" onClick={() => navigate(BACK_TO)}>
            {t('ra.action.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? t('screens.ocrTesting.creating') : t('screens.ocrTesting.createCase')}
          </Button>
        </Box>
      </Card>
    </Stack>
  );
};
